import { useState, useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  CircleStackIcon,
  Cog6ToothIcon,
  CpuChipIcon,
  CreditCardIcon,
  DocumentTextIcon,
  KeyIcon,
  SignalIcon,
  UsersIcon,
  WalletIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores';

interface UserRow {
  role: string;
}

interface ActivityRow {
  id: string;
  type: string;
  action: string;
  user: string;
  details: string;
  timestamp: string;
}

interface DashboardStats {
  totalUsers: number;
  adminCount: number;
  editorCount: number;
  viewerCount: number;
  liveConnections: number;
  liveUsers: number;
  aiProviders: number;
  aiActive: number;
  storageProviders: number;
  storageActive: number;
  paymentProviders: number;
  paymentActive: number;
  socialProviders: number;
  socialActive: number;
  cryptoWallets: number;
  cryptoActive: number;
  aiPrompts: number;
}

interface QuickLink {
  to: string;
  label: string;
  description: string;
  icon: ReactNode;
}

const quickLinks: QuickLink[] = [
  {
    to: '/admin/users',
    label: 'Kullanıcılar',
    description: 'Rolleri yönet',
    icon: <UsersIcon />,
  },
  {
    to: '/admin/activity',
    label: 'Etkinlik',
    description: 'SignalR ve günlük',
    icon: <SignalIcon />,
  },
  {
    to: '/admin/storage-providers',
    label: 'Depolama',
    description: 'S3, MinIO, GCS…',
    icon: <CircleStackIcon />,
  },
  {
    to: '/admin/social-providers',
    label: 'Social Login',
    description: 'OAuth sağlayıcıları',
    icon: <KeyIcon />,
  },
  {
    to: '/admin/ai-providers',
    label: 'AI Sağlayıcılar',
    description: 'Model ve API anahtarları',
    icon: <CpuChipIcon />,
  },
  {
    to: '/admin/ai-prompts',
    label: 'AI Promptlar',
    description: 'Sistem promptları',
    icon: <DocumentTextIcon />,
  },
  {
    to: '/admin/payment-providers',
    label: 'Ödeme',
    description: 'Stripe, iyzico…',
    icon: <CreditCardIcon />,
  },
  {
    to: '/admin/crypto-wallets',
    label: 'Kripto',
    description: 'Cüzdan adresleri',
    icon: <WalletIcon />,
  },
  {
    to: '/admin/disclaimers',
    label: 'Disclaimers',
    description: 'Yasal metinler',
    icon: <DocumentTextIcon />,
  },
  {
    to: '/admin/install',
    label: 'Kurulum',
    description: 'Migrasyon ve seed',
    icon: <WrenchScrewdriverIcon />,
  },
  {
    to: '/admin/settings',
    label: 'Ayarlar',
    description: 'Yapılandırma merkezi',
    icon: <Cog6ToothIcon />,
  },
];

async function fetchJson<T>(url: string, token: string | null): Promise<T | null> {
  try {
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) return null;
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

function countActive(items: Array<{ isActive?: boolean }> | null): { total: number; active: number } {
  if (!items) return { total: 0, active: 0 };
  return {
    total: items.length,
    active: items.filter((i) => i.isActive).length,
  };
}

function formatRelativeTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'az önce';
  if (mins < 60) return `${mins} dk önce`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} sa önce`;
  const days = Math.floor(hours / 24);
  return `${days} gün önce`;
}

export function Dashboard() {
  const { accessToken } = useAuthStore();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentActivity, setRecentActivity] = useState<ActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');

      const [
        users,
        signalr,
        activities,
        aiProviders,
        aiPrompts,
        storage,
        payment,
        social,
        crypto,
      ] = await Promise.all([
        fetchJson<UserRow[]>('/api/admin/users', accessToken),
        fetchJson<{ totalConnections: number; uniqueUsers: number }>(
          '/api/admin/signalr-connections',
          accessToken,
        ),
        fetchJson<ActivityRow[]>('/api/admin/activity-logs?take=8', accessToken),
        fetchJson<Array<{ isActive: boolean }>>('/api/admin/ai-providers', accessToken),
        fetchJson<unknown[]>('/api/admin/ai-prompts', accessToken),
        fetchJson<Array<{ isActive: boolean }>>('/api/admin/storage-providers', accessToken),
        fetchJson<Array<{ isActive: boolean }>>('/api/admin/payment-providers', accessToken),
        fetchJson<Array<{ isActive: boolean }>>('/api/admin/auth-providers', accessToken),
        fetchJson<Array<{ isActive: boolean }>>('/api/admin/crypto-wallets', accessToken),
      ]);

      if (cancelled) return;

      if (!users) {
        setError('Dashboard verileri yüklenemedi.');
        setLoading(false);
        return;
      }

      const ai = countActive(aiProviders);
      const st = countActive(storage);
      const pay = countActive(payment);
      const soc = countActive(social);
      const cry = countActive(crypto);

      setStats({
        totalUsers: users.length,
        adminCount: users.filter((u) => u.role === 'admin' || u.role === 'system').length,
        editorCount: users.filter((u) => u.role === 'editor').length,
        viewerCount: users.filter((u) => u.role === 'viewer').length,
        liveConnections: signalr?.totalConnections ?? 0,
        liveUsers: signalr?.uniqueUsers ?? 0,
        aiProviders: ai.total,
        aiActive: ai.active,
        storageProviders: st.total,
        storageActive: st.active,
        paymentProviders: pay.total,
        paymentActive: pay.active,
        socialProviders: soc.total,
        socialActive: soc.active,
        cryptoWallets: cry.total,
        cryptoActive: cry.active,
        aiPrompts: aiPrompts?.length ?? 0,
      });
      setRecentActivity(activities ?? []);
      setLoading(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  if (loading) {
    return <div className="admin-loading">Yükleniyor…</div>;
  }

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <div>
          <h1>Kontrol Paneli</h1>
          <p>Sistem özeti, canlı bağlantılar ve hızlı erişim</p>
        </div>
      </header>

      {error ? <div className="admin-error">{error}</div> : null}

      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-icon" aria-hidden>
            <UsersIcon />
          </div>
          <div className="admin-stat-content">
            <div className="admin-stat-value">{stats?.totalUsers ?? 0}</div>
            <div className="admin-stat-label">Toplam kullanıcı</div>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-icon" aria-hidden>
            <SignalIcon />
          </div>
          <div className="admin-stat-content">
            <div className="admin-stat-value">{stats?.liveConnections ?? 0}</div>
            <div className="admin-stat-label">
              Canlı bağlantı · {stats?.liveUsers ?? 0} kullanıcı
            </div>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-icon" aria-hidden>
            <CpuChipIcon />
          </div>
          <div className="admin-stat-content">
            <div className="admin-stat-value">
              {stats?.aiActive ?? 0}
              <span className="admin-stat-suffix">/{stats?.aiProviders ?? 0}</span>
            </div>
            <div className="admin-stat-label">Aktif AI sağlayıcı</div>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-icon" aria-hidden>
            <CircleStackIcon />
          </div>
          <div className="admin-stat-content">
            <div className="admin-stat-value">
              {stats?.storageActive ?? 0}
              <span className="admin-stat-suffix">/{stats?.storageProviders ?? 0}</span>
            </div>
            <div className="admin-stat-label">Aktif depolama</div>
          </div>
        </div>
      </div>

      <div className="admin-dashboard-grid">
        <section className="admin-card admin-dashboard-panel">
          <div className="admin-card-header">
            <h2>Rol dağılımı</h2>
          </div>
          <div className="admin-card-body">
            <div className="admin-role-breakdown">
              <div className="admin-role-row">
                <span>Admin / System</span>
                <strong>{stats?.adminCount ?? 0}</strong>
              </div>
              <div className="admin-role-row">
                <span>Editor</span>
                <strong>{stats?.editorCount ?? 0}</strong>
              </div>
              <div className="admin-role-row">
                <span>Viewer</span>
                <strong>{stats?.viewerCount ?? 0}</strong>
              </div>
            </div>
            <div className="admin-provider-summary">
              <div className="admin-provider-chip">
                <span>Ödeme</span>
                <strong>
                  {stats?.paymentActive ?? 0}/{stats?.paymentProviders ?? 0}
                </strong>
              </div>
              <div className="admin-provider-chip">
                <span>Social</span>
                <strong>
                  {stats?.socialActive ?? 0}/{stats?.socialProviders ?? 0}
                </strong>
              </div>
              <div className="admin-provider-chip">
                <span>Kripto</span>
                <strong>
                  {stats?.cryptoActive ?? 0}/{stats?.cryptoWallets ?? 0}
                </strong>
              </div>
              <div className="admin-provider-chip">
                <span>Prompt</span>
                <strong>{stats?.aiPrompts ?? 0}</strong>
              </div>
            </div>
          </div>
        </section>

        <section className="admin-card admin-dashboard-panel">
          <div className="admin-card-header admin-card-header-row">
            <h2>Son etkinlikler</h2>
            <Link to="/admin/activity" className="admin-text-link">
              Tümü
            </Link>
          </div>
          <div className="admin-card-body admin-card-body-flush">
            {recentActivity.length === 0 ? (
              <div className="admin-empty-state admin-empty-state-compact">
                <p>Henüz kayıtlı etkinlik yok.</p>
              </div>
            ) : (
              <ul className="admin-activity-feed">
                {recentActivity.map((item) => (
                  <li key={item.id} className="admin-activity-feed-item">
                    <div className="admin-activity-feed-main">
                      <span className="admin-activity-feed-action">{item.action}</span>
                      <span className="admin-activity-feed-meta">
                        {item.user}
                        {item.details ? ` · ${item.details}` : ''}
                      </span>
                    </div>
                    <time className="admin-activity-feed-time" dateTime={item.timestamp}>
                      {formatRelativeTime(item.timestamp)}
                    </time>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      <div className="admin-quick-actions">
        <h2>Hızlı erişim</h2>
        <div className="admin-actions-grid">
          {quickLinks.map((link) => (
            <Link key={link.to} to={link.to} className="admin-action-card">
              <span className="admin-action-icon" aria-hidden>
                {link.icon}
              </span>
              <span className="admin-action-label">{link.label}</span>
              <span className="admin-action-desc">{link.description}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
